<template>
  <div class="h-screen flex flex-col bg-pitch-black text-total-white">
    <TopNavbar />
    <div class="flex-1 overflow-y-auto p-4">
        <a @click="goBack" class="mt-2 text-prime-golden text-xl flex gap-2 items-center">
            <img class="w-6 h-6" src="@/assets/images/left-chevron.svg" alt="Back Button" />
        </a>
        <h1 class="text-3xl font-bold my-4 flex justify-center items-center gap-2 w-full">
          Merge Contact 
          <img v-if="isLoading" src="@/assets/images/spinner.svg" alt="Loading Spinner" class="text-prime-golden w-8 h-8">
        </h1>
        <form @submit.prevent="mergeContact" class="space-y-4" :class="{ 'opacity-50': isLoading }" :disabled="isLoading">
            <div class="flex justify-between items-center gap-4 mb-4">
                <div
                    :class="[
                        'flex flex-1 relative rounded-md items-center border border-dark-grey gap-1',
                        dropdownOpen ? 'bg-dark-grey py-4' : 'bg-eerie-black py-2',
                    ]"
                >
                    <div class="w-full relative bg-transparent px-2 flex items-center">
                        <div
                            v-if="!dropdownOpen"
                            @click="toggleDropdown"
                            class="w-full flex justify-between items-center cursor-pointer"
                        >
                            <span class="text-sm font-semibold text-total-white p-1">
                                {{ selectedContact ? contacts.find(c => c.id === selectedContact)?.first_name + ' ' + contacts.find(c => c.id === selectedContact)?.last_name : "Select a contact" }}
                            </span>
                            <i class="fas fa-chevron-down text-prime-golden mr-1"></i>
                        </div>
                        <div v-else class="w-full flex items-center px-2">
                            <input
                                v-model="searchQuery"
                                type="text"
                                placeholder="Search contacts..."
                                class="w-full bg-transparent border-none text-total-white outline-none"
                            />
                            <button
                                @click="closeDropdown"
                                class="text-xs text-pitch-black px-1 rounded-full bg-ash-grey ml-2"
                            >
                                <i class="fas fa-times"></i>
                            </button>
                        </div>
                        <div
                            v-if="dropdownOpen"
                            class="absolute top-10 mt-1 w-full left-0 bg-eerie-black border border-dark-grey rounded-md z-10"
                        >
                            <ul>
                                <li
                                    v-for="contact in filteredContacts"
                                    :key="contact.id"
                                    @mousedown.prevent="selectContact(contact)"
                                    class="flex justify-between w-full items-center px-4 py-3 bg-pitch-black rounded-md border-b-2 border-total-white-300 hover:bg-dark-grey cursor-pointer"
                                >
                                    <div class="flex gap-6 items-center w-full">
                                        <div
                                            class="w-12 h-12 rounded-full flex items-center justify-center bg-light-grey-translucent-dark border border-light-grey-translucent-dark overflow-hidden"
                                        >
                                            <img
                                                v-if="contact.profile_image_url"
                                                :src="contact.profile_image_url"
                                                alt="Profile Image"
                                                class="w-full h-full rounded-full object-cover"
                                            />
                                            <template v-else>
                                                <i class="fa-solid fa-user text-4xl text-pitch-black -mb-[1px] mt-auto"></i>
                                            </template>
                                        </div>
                                        <div class="text-left flex-1">
                                            <h3 class="text-white font-semibold text-lg">{{ getContactName(contact) }}</h3>
                                            <div class="flex flex-col gap-1 justify-between">
                                              <p class="text-gray-400 text-sm" >{{ contact.email || "-" }}</p>
                                              <p class="text-gray-400 text-sm" >{{ contact.phone || "-" }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            <button type="submit" :disabled="isLoading" class="w-auto py-1 px-3 bg-prime-golden text-pitch-black font-semibold rounded-lg ">Merge</button>
        </form>
    </div>
  </div>
</template>

<script>
import TopNavbar from "@/components/TopNavbar.vue";
import { useContactsStore } from "@/stores/contact";
import { useToast } from "vue-toastification";
export default {
  components: {
    TopNavbar,
  },
  setup() {
    const contactStore = useContactsStore();
    const toast = useToast();
    return { contactStore, toast };
  },
  data() {
    return {
      contacts: [],
      unknownStakeholderId: null,
      selectedContact: null,
      mergeStatus: '',
      isLoading: false,
      dropdownOpen: false,
      searchQuery: '',
    };
  },
  computed: {
    filteredContacts() {
      return this.contacts.filter(contact => {
        const fullName = (contact.first_name + ' ' + contact.last_name).trim();
        return (
          fullName && fullName.length > 0 &&
          fullName.toLowerCase().includes(this.searchQuery.toLowerCase()) &&
          contact.id != this.unknownStakeholderId
        );
      });
    },
  },
  methods: {
    async goBack() {
        this.$router.back();
    },
    async fetchContacts() {
        await this.contactStore.fetchAllContacts();
        this.contacts = this.contactStore.contacts;
    },
    async mergeContact() {
      this.isLoading = true;

      try {
        await this.contactStore.mergeContacts({
          unknown_stakeholder_id: this.$route.params.id,
          named_stakeholder_id: this.selectedContact,
        });

        this.toast.success("Contact merged successfully!");
        if (this.$route.query.projectId) {
          this.$router.push({ name: "ProjectPage", params: { id: this.$route.query.projectId }, query: { tab: "contacts" } });
        } else {
          this.$router.push({ name: "ContactBookPage" });
        }
      } catch (error) {
        console.error("Contact merge failed:", error);
        this.toast.error("Contact merge failed:");
        this.isLoading = false;
        return;
      } finally {
        this.isLoading = false;
      }
    },
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
    },
    closeDropdown() {
      this.dropdownOpen = false;
    },
    selectContact(contact) {
      this.selectedContact = contact.id;
      this.closeDropdown();
    },
    getContactName(contact) {
      return contact.first_name + ' ' + contact.last_name;
    },
  },
  async mounted() {
    this.unknownStakeholderId = this.$route.params.id;
    await this.fetchContacts();
  },
};
</script>

<style scoped>
/* Add any necessary styles here */
</style>
